import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface CollapsibleCellProps {
  /** Cell content — plain text or rich blocks (card lists, Q&A pairs). */
  children: ReactNode;
  /**
   * Content sits in a box with `p-1.5` top padding (e.g. `DiffCell`) — the clamp
   * grows by that padding so exactly four text lines still show.
   */
  inset?: boolean;
  /** `data-testid` root; derives `-content` and `-toggle-button`. */
  testId?: string;
}

/**
 * Table-cell wrapper that shows the first four lines of its content and collapses
 * the rest behind a Show more / Show less toggle, rendered **only when the content
 * overflows**. Clamps by height (`4lh` of the body text's line-height) rather than
 * line-clamp, so card lists and other block content collapse too; overflow is
 * measured from the DOM so it tracks the real column width.
 */
export function CollapsibleCell({
  children,
  inset = false,
  testId = 'collapsible-cell',
}: CollapsibleCellProps) {
  const { t } = useTranslation('common');
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Measure overflow while collapsed. ResizeObserver fires once on observe and on
  // every size change; while expanded the box is unclipped, so the last reading holds.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (!expanded) setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [children, expanded, inset]);

  return (
    <div data-testid={testId}>
      <div
        ref={contentRef}
        data-testid={`${testId}-content`}
        // `text-sm leading-relaxed` matches the cell body text, so `lh` is one rendered line.
        className={cn(
          'relative text-sm leading-relaxed',
          !expanded && ['overflow-hidden', inset ? 'max-h-[calc(4lh+0.375rem)]' : 'max-h-[4lh]'],
        )}
      >
        {children}
      </div>
      {(isOverflowing || expanded) && (
        <Button
          type="button"
          variant="link"
          data-testid={`${testId}-toggle-button`}
          aria-expanded={expanded}
          onClick={(e) => {
            // Keep the toggle local — rows may carry their own click handlers.
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          className="text-primary mt-1 h-auto cursor-pointer p-0 text-xs font-medium"
        >
          {expanded ? t('showLess') : t('showMore')}
        </Button>
      )}
    </div>
  );
}
